import {
  Dimension,
  Display,
  Position,
  Unit,
  Direction,
  Axis,
  Side,
  BorderStyle,
  AlignItems,
  Wrap,
} from './constants'

window.sscounter = 0
window.cccounter = 0

const calculateStyleValue = (styleSize, base, defaultValue) => {
  if (styleSize == null) {
    return defaultValue
  }
  const { unit, value } = styleSize
  switch (unit) {
    case Unit.PX:
      return value
    case Unit.PERCENTAGE:
      return ((base || 0) / 100) * value
  }
}

const calculateBorderWidth = (borderWidth, borderStyle) =>
  (borderStyle || BorderStyle.NONE) === BorderStyle.NONE
    ? 0
    : calculateStyleValue(borderWidth, undefined, 0)

export default class LayoutItem {
  constructor(config, node, parent) {
    if (node._isLayoutItem === true) {
      return node
    }
    window.cccounter++
    this._x = 0
    this._y = 0
    this._isLayoutItem = true
    this._width = config.getSize(node, Dimension.WIDTH) || 0
    this._height = config.getSize(node, Dimension.HEIGHT) || 0

    this._flexSize = null
    this._flexCrossSize = null

    this._givenWidth = 0
    this._givenWidthCached = false
    this._givenHeight = 0
    this._givenHeightCached = false

    this.initialized = false
    this.config = config
    this.node = node
    this.nodes = config.getChildren(this.node) || []
    this.children = []
    this.parent = parent
    const style = config.getStyle(node)
    this.initializeStyle(style)

    this.baseWidth = 0
    this.baseHeight = 0

    this.calculateOffsets()
    if (typeof config.cacheLayout === 'function') {
      config.cacheLayout(node, this)
    }
  }

  initializeStyle(style) {
    if (style === this.style) {
      return
    }
    this.style = style
    this.position = style.position || Position.STATIC
    this.borderTopWidth = calculateBorderWidth(style.borderTopWidth, style.borderTopStyle)
    this.borderRightWidth = calculateBorderWidth(style.borderRightWidth, style.borderRightStyle)
    this.borderBottomWidth = calculateBorderWidth(style.borderBottomWidth, style.borderBottomStyle)
    this.borderLeftWidth = calculateBorderWidth(style.borderLeftWidth, style.borderLeftStyle)
    // Flex
    this.isFlex = style.display === Display.FLEX
    this.isFlexColumn = style.flexDirection === Direction.COLUMN
    this.flexDimension = this.isFlex
      ? this.isFlexColumn
        ? Dimension.HEIGHT
        : Dimension.WIDTH
      : undefined
    this.flexCrossDimension = this.isFlex
      ? this.isFlexColumn
        ? Dimension.WIDTH
        : Dimension.HEIGHT
      : undefined
    this.flexShrink = style.flexShrink != null ? style.flexShrink : 1
    this.flexGrow = style.flexGrow != null ? style.flexGrow : 0

    this.flexParentDimension = this.parent ? this.parent.flexDimension : undefined
    this.flexMaxSize = undefined
    this.flexMinSize = undefined
    if (!this.flexParentDimension) {
      return
    }
    const flexMaxSize =
      this.flexParentDimension === Dimension.WIDTH ? style.maxWidth : style.maxHeight
    const flexMinSize =
      this.flexParentDimension === Dimension.WIDTH ? style.minWidth : style.minHeight
    this.flexMaxSize = calculateStyleValue(flexMaxSize, undefined, Number.MAX_SAFE_INTEGER)
    this.flexMinSize = calculateStyleValue(flexMinSize, undefined, 0)
    const parentStyle = this.parent.style
    if (
      (parentStyle.alignItems || AlignItems.STRETCH) === AlignItems.STRETCH &&
      parentStyle.flexWrap !== Wrap.WRAP &&
      this.position !== Position.ABSOLUTE
    ) {
      this._flexCrossSize = this.parent.getGivenInnerSize(this.parent.flexCrossDimension)
    }
  }

  initializeBase() {
    if (!this.style.percentageValuesExists) {
      return
    }
    const baseWidth = this.parent ? this.parent.getGivenBoxSize(Dimension.WIDTH) : 0
    const baseHeight = this.parent ? this.parent.getGivenBoxSize(Dimension.HEIGHT) : 0
    if (this.baseHeight !== baseHeight) {
      this.baseHeight = baseHeight
      this._givenHeightCached = false
    }
    if (this.baseWidth !== baseWidth) {
      this.baseWidth = baseWidth
      this._givenWidthCached = false
      this.calculateOffsets()
    }
  }

  calculateOffsets() {
    const { style } = this
    this.marginTop = calculateStyleValue(style.marginTop, this.baseWidth, 0)
    this.marginRight = calculateStyleValue(style.marginRight, this.baseWidth, 0)
    this.marginBottom = calculateStyleValue(style.marginBottom, this.baseWidth, 0)
    this.marginLeft = calculateStyleValue(style.marginLeft, this.baseWidth, 0)
    this.paddingTop = Math.max(calculateStyleValue(style.paddingTop, this.baseWidth, 0), 0)
    this.paddingRight = Math.max(calculateStyleValue(style.paddingRight, this.baseWidth, 0), 0)
    this.paddingBottom = Math.max(calculateStyleValue(style.paddingBottom, this.baseWidth, 0), 0)
    this.paddingLeft = Math.max(calculateStyleValue(style.paddingLeft, this.baseWidth, 0), 0)
  }

  get width() {
    return this._width
  }
  get height() {
    return this._height
  }

  // Position
  get top() {
    const { item } = this.getContainingBox()
    return calculateStyleValue(this.style[Position.TOP], item.getBoxSize(Dimension.HEIGHT))
  }
  get right() {
    const { item } = this.getContainingBox()
    return calculateStyleValue(this.style[Position.RIGHT], item.getBoxSize(Dimension.WIDTH))
  }
  get bottom() {
    const { item } = this.getContainingBox()
    return calculateStyleValue(this.style[Position.BOTTOM], item.getBoxSize(Dimension.HEIGHT))
  }
  get left() {
    const { item } = this.getContainingBox()
    return calculateStyleValue(this.style[Position.LEFT], item.getBoxSize(Dimension.WIDTH))
  }

  // Flex styles
  get flexSize() {
    return this._flexSize
  }
  set flexSize(value) {
    if (this.flexParentDimension === Dimension.WIDTH) {
      this._givenWidthCached = false
    } else {
      this._givenHeightCached = false
    }
    this._flexSize = value
  }
  get flexCrossSize() {
    return this._flexCrossSize
  }
  set flexCrossSize(value) {
    if (this.flexParentDimension === Dimension.WIDTH) {
      this._givenHeightCached = false
    } else {
      this._givenWidthCached = false
    }
    this._flexCrossSize = value
  }

  getContainingBox(res) {
    // First call
    if (res == null) {
      return this.parent.getContainingBox({ item: null, offsetX: 0, offsetY: 0 })
    }
    // Positioned parent found
    if (!this.parent || this.position !== Position.STATIC) {
      res.item = this
      return res
    }
    res.offsetX -= this.config.getPosition(this.node, Axis.X)
    res.offsetY -= this.config.getPosition(this.node, Axis.Y)
    return this.parent.getContainingBox(res)
  }

  getStyleSize(base, size, maxSize, minSize, flexBasis) {
    window.sscounter++
    let styleSize = calculateStyleValue(flexBasis || size, base)
    const maxStyleSize = calculateStyleValue(maxSize, base)
    const minStyleSize = calculateStyleValue(minSize, base)
    if (styleSize != null && maxStyleSize != null && styleSize > maxStyleSize) {
      styleSize = maxStyleSize
    }
    if (
      minStyleSize != null &&
      ((styleSize == null && this.width < minStyleSize) ||
        (styleSize != null && styleSize < minStyleSize))
    ) {
      styleSize = minStyleSize
    }

    return styleSize
  }

  /**
   * calculates width given by style
   * dependencies: style object, parent width
   */
  getStyleWidth() {
    const { style } = this
    const flexBasis = this.flexParentDimension === Dimension.WIDTH ? style.flexBasis : undefined
    return this.getStyleSize(this.baseWidth, style.width, style.maxWidth, style.minWidth, flexBasis)
  }

  /**
   * calculates width given by style
   * dependencies: style object, parent height
   */
  getStyleHeight() {
    const { style } = this
    const flexBasis = this.flexParentDimension === Dimension.HEIGHT ? style.flexBasis : undefined
    return this.getStyleSize(
      this.baseHeight,
      style.height,
      style.maxHeight,
      style.minHeight,
      flexBasis,
    )
  }

  getContentSize(dimension) {
    if (dimension === Dimension.WIDTH) {
      return this._contentWidth
    }
    if (dimension === Dimension.HEIGHT) {
      return this._contentHeight
    }
  }

  getMarginSum(dimension) {
    if (dimension === Dimension.WIDTH) {
      return this.marginLeft + this.marginRight
    }
    if (dimension === Dimension.HEIGHT) {
      return this.marginTop + this.marginBottom
    }
  }

  getInnerOffset(sideOrDimension) {
    switch (sideOrDimension) {
      case Side.TOP:
        return this.paddingTop + this.borderTopWidth
      case Side.RIGHT:
        return this.paddingRight + this.borderRightWidth
      case Side.BOTTOM:
        return this.paddingBottom + this.borderBottomWidth
      case Side.LEFT:
        return this.paddingLeft + this.borderLeftWidth
      case Dimension.WIDTH:
        return this.getInnerOffset(Side.LEFT) + this.getInnerOffset(Side.RIGHT)
      case Dimension.HEIGHT:
        return this.getInnerOffset(Side.TOP) + this.getInnerOffset(Side.BOTTOM)
    }
  }

  getGivenInnerSize(dimension) {
    const givenBoxSize = this.getGivenBoxSize(dimension)
    if (givenBoxSize == null) {
      return null
    }

    return givenBoxSize - this.getInnerOffset(dimension)
  }

  getBoxSize(dimension, isBounding) {
    let res = dimension === Dimension.WIDTH ? this.width : this.height

    const innerOffset = this.getInnerOffset(dimension)

    const contentSize = this.getContentSize(dimension)
    if (contentSize != null) {
      res = contentSize + innerOffset
    }

    const givenSize = this.getGivenBoxSize(dimension)
    if (givenSize != null) {
      const useGivenSize = !isBounding || givenSize > res
      if (useGivenSize) {
        res = givenSize
      }
    }

    if (innerOffset > res) {
      res = innerOffset
    }

    return res
  }

  getBoundingSize(dimension) {
    return this.getBoxSize(dimension, true)
  }

  /**
   * Returns box size given by style or flex-parent
   * @param {Dimension} dimension
   */
  getGivenBoxSize(dimension) {
    const isCached =
      dimension === Dimension.WIDTH ? this._givenWidthCached : this._givenHeightCached
    if (isCached) {
      return dimension === Dimension.WIDTH ? this._givenWidth : this._givenHeight
    }

    let res
    const { flexParentDimension, flexSize, flexCrossSize } = this
    const useFlexCrossSize = flexParentDimension != null && dimension !== flexParentDimension
    if (useFlexCrossSize && flexCrossSize != null) {
      res = flexCrossSize - this.getMarginSum(dimension)
    }

    const styleSize = dimension === Dimension.WIDTH ? this.getStyleWidth() : this.getStyleHeight()
    if (styleSize != null) {
      res = styleSize
    }

    const useFlexSize = dimension === flexParentDimension
    if (useFlexSize && flexSize != null) {
      res = flexSize
    }

    if (dimension === Dimension.WIDTH) {
      this._givenWidth = res
      this._givenWidthCached = true
    } else {
      this._givenHeight = res
      this._givenHeightCached = true
    }

    return res
  }

  getOuterSize(dimension) {
    return this.getBoxSize(dimension) + this.getMarginSum(dimension)
  }

  adjustFlexSize(amount) {
    const currentSize =
      this.flexSize != null ? this.flexSize : this.getBoxSize(this.flexParentDimension)
    const newSize = currentSize + amount
    if (newSize > this.flexMaxSize) {
      const diff = newSize - this.flexMaxSize
      amount -= diff
    }
    if (newSize < this.flexMinSize) {
      const diff = this.flexMinSize - newSize
      amount += diff
    }
    this.flexSize = currentSize + amount
    return amount
  }

  adjustPosition(axis, amount) {
    const { getPosition, setPosition } = this.config
    setPosition(this.node, axis, getPosition(this.node, axis) + amount)
  }

  setOuterPosition(axis, value) {
    const margin = axis === Axis.X ? this.marginLeft : this.marginTop
    const result = margin + value
    if (axis === Axis.X) {
      this._x = result
    } else {
      this._y = result
    }
    this.config.setPosition(this.node, axis, result)
  }

  setContentSize(dimension, value) {
    if (dimension === Dimension.WIDTH) {
      this._contentWidth = value
    }
    if (dimension === Dimension.HEIGHT) {
      this._contentHeight = value
    }
  }
}
